import { useEffect, useRef, useState } from 'react';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';
import { useToast } from '../ui/Toast';
import { useCreateAsset } from '../../lib/api/assets';
import { Campaign } from '../../lib/types';
import { CAMPAIGN_TEMPLATES, CampaignTemplate } from '../../lib/templates';

interface Props {
  campaign: Campaign;
}

export const ApplyTemplateButton = ({ campaign }: Props) => {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const createAsset = useCreateAsset();
  const toast = useToast();

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const apply = async (tpl: CampaignTemplate) => {
    setOpen(false);
    setPending(true);
    let created = 0;
    let failed = 0;
    for (const a of tpl.assets) {
      try {
        await createAsset.mutateAsync({
          title: `${campaign.name} - ${a.title_suffix}`,
          category: a.category,
          asset_type: a.asset_type,
          campaign: campaign.id,
          brands: (campaign.brands as string[]) ?? [],
          dedicated_brand: null,
          publish_date: campaign.start_date,
          end_date: null,
          publish_time: null,
          status: 'Not started',
          description: '',
          caption: '',
        });
        created += 1;
      } catch (err) {
        console.error('Template asset failed', a, err);
        failed += 1;
      }
    }
    setPending(false);
    if (failed === 0) {
      toast.push(`Added ${created} placement${created === 1 ? '' : 's'} from "${tpl.name}"`);
    } else {
      toast.push(`Added ${created} of ${tpl.assets.length} (${failed} failed)`);
    }
  };

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      <Button variant="ghost" onClick={() => setOpen((v) => !v)} disabled={pending}>
        <Icon name="sparkle" size={13} />
        {pending ? 'Applying…' : 'Apply template'}
        <Icon name="chevronDown" size={11} />
      </Button>
      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            right: 0,
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,.10)',
            zIndex: 50,
            padding: 4,
            minWidth: 280,
          }}
        >
          {CAMPAIGN_TEMPLATES.map((tpl) => (
            <button
              key={tpl.id}
              type="button"
              role="menuitem"
              onClick={() => apply(tpl)}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '8px 10px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                borderRadius: 6,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-hover)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>{tpl.name}</div>
              {tpl.description && (
                <div style={{ fontSize: 11.5, color: 'var(--ink-4)', marginTop: 2 }}>
                  {tpl.description}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ApplyTemplateButton;
